import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, CocCancellationRequest } from '../utils/transactionStore';
const MakerCocCancellationTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pending, setPending] = useState<CocCancellationRequest[]>([]);
  const [rejected, setRejected] = useState<CocCancellationRequest[]>([]);
  const [selected, setSelected] = useState<CocCancellationRequest | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'resubmit' | 'discard' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => {
    setPending(transactionStore.getPendingCocCancellations());
    setRejected(transactionStore.getRejectedCocCancellations());
  };
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: CocCancellationRequest, action: 'resubmit' | 'discard') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'resubmit') {
        transactionStore.resubmitCocCancellation(selected.id, reason);
        showToast(`COC cancellation resubmitted for ${selected.transactionId}`, 'success');
      } else {
        transactionStore.discardCocCancellation(selected.id, reason);
        showToast(`COC cancellation discarded for ${selected.transactionId}`, 'info');
      }
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const pendingColumns: Column<CocCancellationRequest>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1331" data-mp="1sy15vcqiqsze">{row.id}</Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1332" data-mp="1k57l4zsaz1e5">{row.transactionId}</Typography>
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1333" data-mp="1a8b8chvdft3s">{row.currencyFCY} {row.amountFCY}</Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: () => <Chip label="PENDING" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1334" data-mp="1586r2pmn2uci" />
  }, {
    id: 'requestedAt',
    label: 'Submitted At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1335" data-mp="3cwt0ncc4e4k9">{new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}</Typography>
  }];
  const rejectedColumns: Column<CocCancellationRequest>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1336" data-mp="3qtq207pv74fe">{row.id}</Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1337" data-mp="2y6vk9ikfmj5m">{row.transactionId}</Typography>
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1338" data-mp="37vdh5oplc63z">{row.currencyFCY} {row.amountFCY}</Typography>
  }, {
    id: 'checkerComment',
    label: 'Rejection Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" color="error.main" noWrap sx={{
      maxWidth: 200
    }} data-id="element-1339" data-mp="2aef092h4wh3o">{row.checkerComment}</Typography>
  }, {
    id: 'checkerActedAt',
    label: 'Rejected At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1340" data-mp="kwrijrvenvjk">{new Date(row.checkerActedAt || '').toLocaleString()}</Typography>
  }];
  const rejectedRowActions: RowAction<CocCancellationRequest>[] = [{
    icon: 'lucide:rotate-cw',
    label: 'Resubmit',
    onClick: row => handleAction(row, 'resubmit')
  }, {
    icon: 'lucide:trash-2',
    label: 'Delete',
    onClick: row => handleAction(row, 'discard')
  }];
  return <Box data-id="element-1341" data-mp="zotej0ucu4eb">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-1342" data-mp="zmyvxt6k2ode">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} data-id="element-1343" data-mp="1dnjceip0083q">
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1345" data-mp="18oowppmbuznu"><Typography variant="body2" fontWeight={500} data-id="element-1346" data-mp="18p83ca6j21mk">Pending</Typography><Chip label={pending.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            color: '#F59E0B'
          }} data-id="element-1347" data-mp="3femq5gu6q2si" /></Box>} data-id="element-1344" data-mp="1dlp01srslob9" />
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1349" data-mp="1py0v77yu3s5y"><Typography variant="body2" fontWeight={500} data-id="element-1350" data-mp="1pykeas080mlk">Rejected</Typography><Chip label={rejected.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444'
          }} data-id="element-1351" data-mp="1uxdifttmb192" /></Box>} data-id="element-1348" data-mp="3aifsebbxzxln" />
        </Tabs>
      </Box>

      {activeTab === 0 ? <DataTable title="Pending COC Cancellations" data={pending} columns={pendingColumns} keyField="id" emptyStateMessage="No pending COC cancellations" initialVisibleColumns={['transactionId', 'amountFCY', 'status', 'requestedAt']} data-id="element-1352" data-mp="1l696z435hyiz" /> : <DataTable title="Rejected COC Cancellations" data={rejected} columns={rejectedColumns} rowActions={rejectedRowActions} keyField="id" emptyStateMessage="No rejected COC cancellations" initialVisibleColumns={['transactionId', 'amountFCY', 'checkerComment', 'checkerActedAt']} data-id="element-1353" data-mp="1c4jdlsppkpik" />}

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-1354" data-mp="2siro2ox7f8du">
        <DialogTitle data-id="element-1355" data-mp="9e0cdn87wowb">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1356" data-mp="9ejm4gnps6z9">
            <Icon icon={actionType === 'resubmit' ? 'lucide:rotate-cw' : 'lucide:trash-2'} width={24} color={actionType === 'resubmit' ? '#0EA5E9' : '#EF4444'} data-id="element-1357" data-mp="9bnoyujoxmbv" />
            <Typography variant="h6" fontWeight={600} data-id="element-1358" data-mp="dvgoacdzexz">{actionType === 'resubmit' ? 'Resubmit' : 'Delete'} COC Cancellation</Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1359" data-mp="s4rjsj8na582">
          <Box sx={{
          mt: 1
        }} data-id="element-1360" data-mp="s3zxy04unluz">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-1361" data-mp="o2o57lqoikik">
                  Transaction ID: <strong data-id="element-1362" data-mp="jbek8yg2s5d0">{selected.transactionId}</strong>
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-1363" data-mp="edmr613p2d78">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-1364" data-mp="1bs8pq92hdpty">Original Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-1365" data-mp="1718wbdsklpw7">{selected.resubmitReason || selected.makerReason}</Typography>
                  <Typography variant="caption" color="#EF4444" fontWeight={600} data-id="element-1366" data-mp="1eehtttlyopsy">Checker's Rejection Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-1367" data-mp="1je46o1xvo418">{selected.checkerComment}</Typography>
                </Paper>
              </>}
            <TextField label={`Reason for ${actionType === 'resubmit' ? 'Resubmission' : 'Deleting'}`} placeholder={`Enter reason for ${actionType === 'resubmit' ? 'resubmitting' : 'deleting'} this request...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-1368" data-mp="1scy763ebxek2" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1369" data-mp="2c0p5fngoniet">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-1370" data-mp="2bzdv14sqfceq">Cancel</Button>
          <Button variant="contained" color={actionType === 'resubmit' ? 'primary' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-1372" data-mp="36biwbflirnqc" /> : <Icon icon={actionType === 'resubmit' ? 'lucide:send' : 'lucide:trash'} width={16} data-id="element-1373" data-mp="36ahbijepbmvf" />} data-id="element-1371" data-mp="2g3likyngembd">
            {actionType === 'resubmit' ? 'Resubmit' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default MakerCocCancellationTable;